import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';

import nullOrBool from '../../../services/nullorbool';
import CodeChip from '../../codechip/components/codechip';
import useListItemStyles from './listitemstyles';
import FindButton from './findbutton';
import { RouteFetcherContext } from '@mk-redbox/route-fetcher';

const createFakeHCC = type => ({
  code: '',
  isNew: false,
  version: '',
  category: 'Hcc',
  type
});

const DxSectionListItem = ({
  encounterId,
  dx,
  unapproveDx,
  unremoveDx,
  openAddReasonOptionDialog,
  isMultiselect,
  multiselectItems,
  beginMultiselect,
  endMultiselect,
  selectItemMultiselect,
  deselectItemMultiselect,
  dateOfService,
  isAudit = false,
  isReview = false
}) => {
  const classes = useListItemStyles();
  const backendRoutes = useContext(RouteFetcherContext);

  const isSelectedItem =
    multiselectItems.filter(x => x === dx.encounterCmsCodeId).length > 0;

  const jumpToEncoder = () => {
    const { EncoderJump } = backendRoutes;
    const year = new Date(dateOfService).getUTCMonth() >= 9
      ? new Date(dateOfService).getUTCFullYear() + 1
      : new Date(dateOfService).getUTCFullYear() ;

    window.open(`${EncoderJump}?year=${year}&term=${dx.code}&searchMode=code`, '_blank');
  };

  const filteredMAs = dx.ccList.length > 0
    ? dx.ccList.filter(x => x.type === 'Ma')
    : [];

  const hasEP = dx.ccList.length > 0
    ? dx.ccList.reduce((acc, curr) => {
      if (acc === true) return acc;
      return curr.type === 'Ep';
    }, false)
    : false;

  const hasRX = dx.ccList.length > 0
    ? dx.ccList.reduce((acc, curr) => {
      if (acc === true) return acc;
      return curr.type === 'Rx';
    }, false)
    : false;

  return (
    <div className={
      isSelectedItem === true
        ? `${classes.root} ${classes.multiselectItem}`
        : (dx.isApproved === true ? `${classes.root} ${classes.isApproved}` : (dx.isApproved === false ? `${classes.root} ${classes.isRemoved}` : `${classes.root}`))
    }>
      {/* indicator for if it has been previously approved this year */}
      {
        dx.isPreviouslyValidated === true
          ? (<Badge variant="dot" color="secondary" />)
          : null
      }

      {/* disposition */}
      <div className={classes.dispositionContainer}>
        <Avatar
          className={classes.dispositionContent}
          onDoubleClick={() => endMultiselect()}
          onClick={() => {
            isMultiselect === true
              ? isSelectedItem
                ? deselectItemMultiselect(dx)
                : selectItemMultiselect(dx)
              : beginMultiselect(dx)
          }}
        >
          {dx.disposition}
        </Avatar>
      </div>

      {/* code chips & description */}
      <div className={classes.chipsAndDescriptionContainer}>
        {/* chip list row */}
        <div className={classes.chipListContainer}>
          <div>
            <CodeChip encoding={dx} onClick={jumpToEncoder} />
          </div>

          <div style={{ marginLeft: '0.3em' }}>
          {
            filteredMAs.map(x => (
              <CodeChip key={x.id} encoding={x} title={x.version} />
            ))
          }
          {
            hasEP === true
              ? <CodeChip encoding={createFakeHCC('Ep')} title="EP" />
              : null
          }
          {
            hasRX === true
              ? <CodeChip encoding={createFakeHCC('Rx')} title="RX" />
              : null
          }
          </div>
        </div>

        {/* description */}
        <div className={classes.descriptionContainer}>
          <p
            title={dx.description}
            className={classes.descriptionContent}
          >
            {dx.description}
          </p>
        </div>
      </div>

      {/* action buttons */}
      <div className={classes.actionButtonsContainer}>
        <div>
          <span>
            <span style={{ fontSize: '0.8em' }}>Approved</span>
            <input
              disabled={isReview}
              type="checkbox"
              style={{ width:'25px', height: '25px'}}
              checked={
                (dx.isApproved === null || dx.isApproved === false)
                  ? false
                  : true
              }
              onChange={() => {
                dx.isApproved === true
                  ? unapproveDx(encounterId, [dx.encounterCmsCodeId])
                  : openAddReasonOptionDialog(
                    'Approve',
                    [dx.encounterCmsCodeId]
                  );
              }}
            />
          </span>
        </div>

        <div>
          <span>
            <span style={{ fontSize: '0.8em' }}>Removed</span>
            <input
              disabled={isReview}
              type="checkbox"
              style={{ width:'25px', height: '25px'}}
              checked={
                (dx.isRemoved === null || dx.isRemoved === false)
                  ? false
                  : true
              }
              onChange={() => {
                dx.isRemoved === true
                  ? unremoveDx(encounterId, [dx.encounterCmsCodeId])
                  : openAddReasonOptionDialog(
                    'Remove',
                    [dx.encounterCmsCodeId]
                  );
              }}
            />
          </span>
        </div>

        <div>
          <FindButton code={dx.code} />
        </div>
      </div>
    </div>
  );
};

DxSectionListItem.propTypes = {
  encounterId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  dx: PropTypes.shape({
    encounterCmsCodeId: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    isApproved: nullOrBool,
    isRemoved: PropTypes.bool.isRequired,
    disposition: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    ccList: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      category: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })).isRequired
  }).isRequired,
  unapproveDx: PropTypes.func.isRequired,
  unremoveDx: PropTypes.func.isRequired,
  openAddReasonOptionDialog: PropTypes.func.isRequired,
  isAudit: PropTypes.bool.isRequired,
  isReview: PropTypes.bool.isRequired,
  isMultiselect: PropTypes.bool.isRequired,
  multiselectItems: PropTypes.array.isRequired,
  beginMultiselect: PropTypes.func.isRequired,
  endMultiselect: PropTypes.func.isRequired,
  selectItemMultiselect: PropTypes.func.isRequired,
  deselectItemMultiselect: PropTypes.func.isRequired
};

export default DxSectionListItem;
